/*
ExtendedDate can be used similar to JavaScript's Date, but with a few extras:
- create a Date in one of these ways easily:
  new ExtendedDate('01.01.2023')
  new ExtendedDate('2023-01-01')
  new ExtendedDate(new Date())
- get a formatted Date easily:
  const mydate = new ExtendedDate(whatever)
  console.log(mydate.dmy) // 01.01.2023
  console.log(mydate.ymd) // 2023-01-01
*/

const ariaDateTimeFormatCache = new Map<string, Intl.DateTimeFormat>()

export class ExtendedDate extends Date {
  constructor (date, ...rest) {
    if (typeof date === 'string') {
      const matches = date.match(/^([0-9]{2})\.([0-9]{2})\.([0-9]{4})$/)
      if (matches) {
        date = `${matches[3]}-${matches[2]}-${matches[1]}`
        // Convert date to UTC timezone
        date = new Date(date)
        // * 60000 to turn minutes to milliseconds
        const userTimezoneOffset = date.getTimezoneOffset() * 60000
        date = new Date(date.getTime() + userTimezoneOffset)
      }
      if (date === '') {
        date = null
      }
    }
    super(date, ...rest)
  }

  static today () {
    const date = new ExtendedDate(new Date())
    date.setHours(0, 0, 0, 0)
    return date
  }

  getRealMonth () {
    return this.getMonth() + 1
  }

  getRealWeekday () {
    return this.getDay() || 7
  }

  getMonthDays () {
    const date = new ExtendedDate(this.getFullYear(), this.getMonth() + 1, 0)
    return date.getDate()
  }

  getFirstDayOfMonth () {
    const date = this.clone()
    date.setDate(1)
    return date
  }

  #getAriaDateTimeFormatFromCache (locale: string): Intl.DateTimeFormat {
    let formatter = ariaDateTimeFormatCache.get(locale)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long'
      })
      ariaDateTimeFormatCache.set(locale, formatter)
    }
    return formatter
  }

  getAria (lang) {
    const langNew = (lang === 'de') ? 'de-DE' : 'en-GB'
    return this.#getAriaDateTimeFormatFromCache(langNew).format(this)
  }

  addDays (days) {
    const date = this.clone()
    date.setDate(date.getDate() + days)
    return date
  }

  addMonths (months) {
    const date = this.clone()
    date.setMonth(date.getMonth() + months)
    return date
  }

  equals (date: ExtendedDate | null): boolean {
    if (date instanceof ExtendedDate) {
      return this.ymd === date.ymd
    }

    return false
  }

  static equalsStatic (date1: string, date2: ExtendedDate | null): boolean {
    const date = new ExtendedDate(date1)
    return date.equals(date2)
  }

  static countNightsStatic (from, to) {
    const fromDate = new ExtendedDate(from)
    const toDate = new ExtendedDate(to)
    return Math.round((toDate.getTime() - fromDate.getTime()) / 1000 / 24 / 60 / 60)
  }

  isAfter (date: ExtendedDate | null, includeDate: boolean = false): boolean {
    return (this.ymd > date?.ymd) || (includeDate && this.equals(date))
  }

  isBefore (date: ExtendedDate | null, includeDate: boolean = false): boolean {
    return (this.ymd < date?.ymd) || (includeDate && this.equals(date))
  }

  isBetween (from: ExtendedDate | null, to: ExtendedDate | null, includeFromAndTo: boolean = true) {
    return this.isAfter(from, includeFromAndTo) && this.isBefore(to, includeFromAndTo)
  }

  isPast () {
    return this.constructor.today().isAfter(this)
  }

  isValid () {
    const time = this.getTime()
    return !isNaN(time) && time !== 0
  }

  clone () {
    return new ExtendedDate(this)
  }

  #getLocalCalendarPartsCache: { year: string, month: string, day: string } | null = null
  #getLocalCalendarParts (): { year: string, month: string, day: string } | null {
    if (this.#getLocalCalendarPartsCache) {
      return this.#getLocalCalendarPartsCache
    }
    const time = this.getTime()
    if (isNaN(time) || time === 0) {
      return null
    }
    const shifted = new Date(time - this.getTimezoneOffset() * 60000)
    this.#getLocalCalendarPartsCache = {
      year: String(shifted.getUTCFullYear()),
      month: String(shifted.getUTCMonth() + 1).padStart(2, '0'),
      day: String(shifted.getUTCDate()).padStart(2, '0')
    }
    return this.#getLocalCalendarPartsCache
  }

  #dmy_cache: string | null = null
  get dmy () {
    if (this.#dmy_cache) {
      return this.#dmy_cache
    }
    const parts = this.#getLocalCalendarParts()
    if (!parts) {
      return ''
    }

    this.#dmy_cache = `${parts.day}.${parts.month}.${parts.year}`
    return this.#dmy_cache
  }

  #ymd_cache: string | null = null
  get ymd () {
    if (this.#ymd_cache) {
      return this.#ymd_cache
    }
    const parts = this.#getLocalCalendarParts()
    if (!parts) {
      return ''
    }
    this.#ymd_cache = `${parts.year}-${parts.month}-${parts.day}`
    return this.#ymd_cache
  }
}
